import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  Itinerary,
  ItineraryLatenessDetail,
  ItineraryStopLateness,
} from 'src/common/external/bambi-api/bambiApi';

export interface ItineraryState {
  stopsByTripId: Record<
    string,
    Partial<Record<'pickup' | 'dropoff', ItineraryStopLateness>> | undefined
  >;
}

export const initialState: ItineraryState = {
  stopsByTripId: {},
};

export type ItineraryStopsByTripLocationType = Partial<
  Record<'pickup' | 'dropoff', ItineraryStopLateness>
>;

export function getStopsByTripId(
  itinerary?: Itinerary,
  itineraryLateness?: ItineraryLatenessDetail
) {
  if (!itinerary || !itineraryLateness) {
    return {};
  }

  return itinerary.stops.reduce<
    Record<string, ItineraryStopsByTripLocationType>
  >((stopsByTripId, stop, i) => {
    const detail = itineraryLateness.stops[i] as
      | ItineraryStopLateness
      | undefined;

    // The lists are mirrored, but just in case...
    if (!detail) {
      return stopsByTripId;
    }

    stop.trips.forEach((trip) => {
      stopsByTripId[trip.id] = {
        [trip.purpose]: detail,
        ...stopsByTripId[trip.id],
      };
    });

    return stopsByTripId;
  }, {});
}

export const itinerarySlice = createSlice({
  name: 'itinerary',
  initialState,
  reducers: {
    reset(state) {
      state.stopsByTripId = initialState.stopsByTripId;
    },
    cacheTripItineraryLateness(
      state,
      action: PayloadAction<{
        itinerary: Itinerary;
        itineraryLateness: ItineraryLatenessDetail;
      }>
    ) {
      const { itinerary, itineraryLateness } = action.payload;
      state.stopsByTripId = {
        ...state.stopsByTripId,
        ...getStopsByTripId(itinerary, itineraryLateness),
      };
    },
  },
});

export const { cacheTripItineraryLateness, reset } = itinerarySlice.actions;
